import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import {
  getDataDetails, postDataComment, putLabels,
} from 'actionHub/utils/actionHubApi'
import {
  actionHubFetchingDataDetail, actionHubFetchedDataDetail, actionHubUpdateDataLabelValue, validateDataLabel, submittingDataLabels, submittedDataLabels, submittingDataComment, submittedDataComment,
} from 'actionHub/redux/actions'
import useApp from 'hooks/useApp'
import { getUserById } from 'utils/api/job'
import { notify } from 'actions/app'
import {
  actionHubFetchingCommentAuthors,
  actionHubFetchedCommentAuthor,
} from '../redux/actions'

export default function useData(actionSetId, taskId, dataId) {
  const dispatch = useDispatch()
  const { showNotification } = useApp()

  const data = useSelector(state => state.actionHub.data[dataId]) || null
  const needsLoading = dataId != null && (data === null || !data._isFetched)
  const {
    displayColumns: columns, idColumns,
  } = useSelector(state => state.actionHub.dataLevel)

  useEffect(() => {
    if (needsLoading) {
      dispatch(actionHubFetchingDataDetail(actionSetId, taskId, dataId))

      getDataDetails(actionSetId, taskId, dataId)
        .then(({ data: responseData }) => {
          dispatch(actionHubFetchedDataDetail(actionSetId, taskId, dataId, responseData))

          if (responseData.comments && responseData.comments.length > 0) {
            const uniqueUserIds = [...new Set(responseData.comments.map(c => c.userId))]

            dispatch(actionHubFetchingCommentAuthors())

            Promise.all(
              uniqueUserIds.map(userId => getUserById(userId)
                .then(({ data: authorData }) => {
                  dispatch(actionHubFetchedCommentAuthor(userId, authorData))
                })
                .catch((e) => {
                  showNotification('Failed to fetch comment author', e, 'error')
                  return null
                })),
            )
          }
        })
        .catch((e) => {
          showNotification('Could not load data, please refresh the page and contact support if issues persist.', e, 'error')
        })
    }
  }, [dispatch, actionSetId, taskId, dataId, needsLoading, showNotification])

  const setLabelValue = (labelId, value) => {
    dispatch(actionHubUpdateDataLabelValue(actionSetId, dataId, labelId, value))
  }

  const validateLabel = (labelId) => {
    dispatch(validateDataLabel(actionSetId, dataId, labelId))
  }

  const submitDataLabels = () => {
    dispatch(submittingDataLabels(actionSetId, taskId, dataId))
    putLabels(actionSetId, 1, dataId, data.labels)
      .then(() => {
        dispatch(submittedDataLabels(actionSetId, taskId, dataId, data.labels))
        showNotification('Review findings saved')
      })
      .catch((e) => {
        showNotification('Could not save changes, please refresh the page and contact support if issues persist.', e, 'error')
      })
  }

  const submitDataComment = async (comment) => {
    dispatch(submittingDataComment(actionSetId, taskId, dataId))

    try {
      const { data: responseData } = await postDataComment(actionSetId, dataId, comment, [], 0)

      dispatch(actionHubFetchingCommentAuthors())
      try {
        const { data: authorData } = await getUserById(responseData.userId)
        dispatch(actionHubFetchedCommentAuthor(responseData.userId, authorData))
      } catch (e) {
        showNotification('Failed to fetch comment author', e, 'error')
      }

      dispatch(submittedDataComment(actionSetId, taskId, dataId, responseData))
    } catch (e) {
      showNotification('Could not save comment, please refresh the page and contact support if issues persist.', e, 'error')
    }
  }

  return {
    data,
    columns: columns.map(x => ({ ...x, idColumn: idColumns.includes(x.name) })),
    setLabelValue,
    validateLabel,
    submitDataLabels,
    submitDataComment,
  }
}
